import type { Middleware, MiddlewareCallbackParams } from 'openapi-fetch'
import type { MaintenanceCapability, MaintenanceInfo } from './maintenanceStore'

/**
 * The second middleware — issue #274's decision 2.
 *
 * `authMiddleware.ts` is the first: `api.use()` runs every registered
 * middleware in order, so this is additive rather than a replacement. Same
 * narrowed parameter type for the same reason that file gives — honest about
 * the one field this middleware reads (`response`), and a test can call
 * `onResponse({ response })` without fabricating a `MiddlewareCallbackParams` it
 * does not use.
 */
export type MaintenanceMiddleware = Middleware & {
  onResponse: (
    params: Pick<MiddlewareCallbackParams & { response: Response }, 'response'>,
  ) => Promise<Response | undefined>
}

/**
 * Is this a URL safe to put in an `href`?
 *
 * `Maintenance.tsx` renders `statusUrl` as `href={statusUrl}` on a real
 * anchor. React does not sanitise `href`, so a server-supplied
 * `javascript:alert(document.cookie)` would execute on click — and validating
 * the field as a non-empty string, which is all `parseMaintenanceBody` asked of
 * it before, checks that something is THERE rather than that it is safe to
 * navigate to.
 * It needs a hostile or compromised backend to matter, which is why this is
 * defence in depth rather than a live hole; it is also two lines, and the
 * alternative is a screen that turns a 503 body into script execution.
 *
 * **Parsed, not pattern-matched.** The URL parser strips tab and newline
 * characters before reading the scheme, so `java\nscript:alert(1)` and
 * `  javascript:alert(1)` both normalise to a `javascript:` protocol and are
 * caught here. A `startsWith('http')` test — the obvious cheap version —
 * passes `javascript:` the moment an attacker embeds a newline, and would
 * also accept `httpx:`. Asking the parser what the scheme actually IS is the
 * only version that answers the question being asked.
 *
 * **Allow-list, not a deny-list.** `javascript:` is the one everybody thinks
 * of, but `data:` and `vbscript:` are the same class of problem, and the next
 * scheme is not knowable from here. Two schemes are admissible and everything
 * else — including a relative path, which has no scheme and cannot be a
 * status page on another host — is refused.
 */
function isSafeHttpUrl(value: string): boolean {
  let parsed: URL
  try {
    parsed = new URL(value)
  } catch {
    // Not absolute, or not parseable at all. A status page lives on a host
    // this app does not serve, so a relative URL is malformed for this field
    // rather than merely unusual.
    return false
  }
  return parsed.protocol === 'https:' || parsed.protocol === 'http:'
}

/**
 * Turn an unknown 503 payload into a `MaintenanceInfo`, or refuse it outright.
 *
 * **A malformed or absent body must not render `<Maintenance>` with `undefined`
 * in the heading** — the issue's own requirement. This function is the gate:
 * `heading` and `body` are required and validated as non-empty strings, and
 * every OPTIONAL field, if present at all, is validated too rather than passed
 * through unchecked — a `returnEta: 42` fails the whole body rather than
 * reaching the screen as a number where a preformatted string was promised.
 *
 * `statusUrl` gets a second question asked of it beyond its type, because it is
 * the one field that reaches the DOM as something the browser will ACT on
 * rather than merely display — see `isSafeHttpUrl`.
 *
 * Exported and pure, on purpose: rule 8 wants the property under test, not a
 * proxy, and the property here is "this exact shape is accepted, this exact
 * shape is refused" — independent of any network plumbing.
 */
export function parseMaintenanceBody(value: unknown): MaintenanceInfo | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null
  const v = value as Record<string, unknown>

  if (typeof v.heading !== 'string' || v.heading.trim() === '') return null
  if (typeof v.body !== 'string' || v.body.trim() === '') return null

  const info: MaintenanceInfo = { heading: v.heading, body: v.body }

  if ('returnEta' in v) {
    if (typeof v.returnEta !== 'string' || v.returnEta.trim() === '') return null
    info.returnEta = v.returnEta
  }

  if ('statusUrl' in v) {
    if (typeof v.statusUrl !== 'string' || v.statusUrl.trim() === '') return null
    if (!isSafeHttpUrl(v.statusUrl)) return null
    info.statusUrl = v.statusUrl
  }

  if ('capabilities' in v) {
    if (!Array.isArray(v.capabilities)) return null
    const capabilities: MaintenanceCapability[] = []
    for (const item of v.capabilities) {
      if (typeof item !== 'object' || item === null || Array.isArray(item)) return null
      const c = item as Record<string, unknown>
      if (typeof c.label !== 'string' || c.label.trim() === '') return null
      if (typeof c.statusLabel !== 'string' || c.statusLabel.trim() === '') return null
      if (typeof c.available !== 'boolean') return null
      capabilities.push({ label: c.label, statusLabel: c.statusLabel, available: c.available })
    }
    info.capabilities = capabilities
  }

  return info
}

/**
 * Recognise a 503 maintenance body and report it through `onDetect`.
 *
 * **Never mutates or consumes the response `openapi-fetch` itself still has to
 * read.** `response.clone()` is what makes that true — `Response.json()`
 * consumes the body stream once, and without the clone this middleware would
 * read it out from under the generated client, which then sees an already-used
 * body on every request it makes. The clone is thrown away after this reads it;
 * the original `response` is returned untouched (`undefined`, meaning
 * "unchanged" — same convention `authMiddleware.ts` documents for its own
 * `onRequest` returning the mutated `request`, here inverted because nothing
 * about the response needs to change).
 *
 * **Three outcomes, and only one of them calls `onDetect` with a fresh notice:**
 *
 *   - **not a 503** — `onDetect(null)` clears any active notice. A request that
 *     got a real answer is the one signal this client has that maintenance
 *     ended; see `maintenanceStore.ts`.
 *   - **503, and the body parses** — `onDetect(info)` sets (or replaces) the
 *     active notice.
 *   - **503, but the body is missing, not JSON, or fails `parseMaintenanceBody`**
 *     — `onDetect` is not called at all. The state is left exactly as it was:
 *     an ordinary 503 with no usable body is not evidence maintenance ended
 *     (the status says otherwise), and it is not a body this component can
 *     render either — inventing a placeholder heading is the fabrication this
 *     whole batch has been refusing. The request instead surfaces as an
 *     ordinary failure through the five-state contract on whichever surface
 *     made it, same as any other error status this client does not have a
 *     specific handler for.
 */
export function createMaintenanceMiddleware(
  onDetect: (info: MaintenanceInfo | null) => void,
): MaintenanceMiddleware {
  return {
    async onResponse({ response }) {
      if (response.status !== 503) {
        onDetect(null)
        return undefined
      }

      let raw: unknown
      try {
        raw = await response.clone().json()
      } catch {
        // No body, or a body that is not JSON. Leave the existing state alone
        // — see the docblock above.
        return undefined
      }

      const info = parseMaintenanceBody(raw)
      if (info) onDetect(info)
      return undefined
    },
  }
}
